export const dummyDetailedReport = {
  id: "preview-result",
  holland_code_id: "1",
  mbti_type_id: "8",
  name: "Visionary Doer",
  simple_desc:
    "A Visionary Doer blends creative imagination with practical action. They are energized by new ideas, motivated by meaningful impact, and happiest when they can turn possibilities into something real.",
  image_urls:
    "https://i.ibb.co/1dWcCyp/Firefly-3d-charecter-CEO-3d-icon-blender-colourful-green-isolated-background-s-750-v-6-0-509.png",
  voice_summary:
    "This sample report describes a Visionary Doer: someone who combines energy, empathy, creativity, and hands-on problem solving to make ideas useful in the real world.",
  overview:
    "You are a creative, action-oriented learner who sees possibilities quickly and enjoys testing them in practical ways. You like work that feels meaningful, visible, and useful. You bring energy to groups, but you also need room to explore your own ideas and move at a pace that keeps you engaged.",
  strength_overview: [
    {
      title: "Action-Oriented Creativity",
      description: "You enjoy turning ideas into visible results instead of leaving them as concepts.",
    },
    {
      title: "People-First Energy",
      description: "You naturally motivate others and make collaborative spaces feel more open.",
    },
    {
      title: "Practical Curiosity",
      description: "You ask how things work and quickly look for ways to improve them.",
    },
    {
      title: "Flexible Problem Solving",
      description: "You adapt when plans change and can find another path without losing momentum.",
    },
    {
      title: "Purposeful Confidence",
      description: "You are most committed when your work connects to a clear human outcome.",
    },
    {
      title: "Hands-On Learning",
      description: "You understand best through building, experimenting, practicing, and reflecting.",
    },
  ],
  figures: {
    description:
      "You are inspired by people who combine imagination with execution, especially when their ideas create useful change for others.",
    celebrities: [
      {
        name: "Elon Musk",
        description: "A bold builder who turns ambitious technical ideas into practical systems.",
        image: "/top-card-char.webp",
      },
      {
        name: "Richard Branson",
        description: "An energetic entrepreneur known for people-focused risk taking and new ventures.",
        image: "/top-card-char.webp",
      },
      {
        name: "Jamie Oliver",
        description: "A hands-on creator who uses practical skills to teach, improve, and inspire.",
        image: "/top-card-char.webp",
      },
      {
        name: "Temple Grandin",
        description: "A practical thinker who improves systems by observing details others miss.",
        image: "/top-card-char.webp",
      },
      {
        name: "Boyan Slat",
        description: "A young innovator who translates environmental concern into engineered action.",
        image: "/top-card-char.webp",
      },
    ],
  },
  interests: {
    description:
      "Your strongest interests sit where creative thinking, useful tools, and real-world outcomes meet.",
    areas: [
      {
        title: "Creative Technology",
        description: "Using tools, systems, or media to make new ideas tangible.",
        percentage: "92",
      },
      {
        title: "Social Impact Projects",
        description: "Solving problems that improve everyday life for other people.",
        percentage: "86",
      },
      {
        title: "Hands-On Design",
        description: "Building, repairing, prototyping, or testing things directly.",
        percentage: "81",
      },
      {
        title: "Communication & Leadership",
        description: "Bringing people together around a clear goal or story.",
        percentage: "74",
      },
    ],
  },
  workfit: [
    {
      title: "Project-Based Roles",
      description: "You do well when goals are clear and the route allows creativity.",
    },
    {
      title: "Collaborative Teams",
      description: "You thrive around people who exchange ideas and move quickly.",
    },
    {
      title: "Visible Outcomes",
      description: "You stay motivated when you can see progress and impact.",
    },
    {
      title: "Flexible Environments",
      description: "You perform best with enough structure to focus and enough freedom to explore.",
    },
  ],
  books: [
    {
      title: "Designing Your Life",
      description: "A practical guide to testing career ideas and building an intentional path.",
    },
    {
      title: "Creative Confidence",
      description: "A useful read for trusting ideas and learning through experimentation.",
    },
    {
      title: "The Practice",
      description: "A short, grounded book about creating consistently and sharing work.",
    },
  ],
  paths: [
    {
      title: "Start With Experiments",
      description: "Prototype small versions of your ideas before committing to a large plan.",
    },
    {
      title: "Build a Feedback Loop",
      description: "Share work early with mentors, teammates, or users to sharpen your direction.",
    },
    {
      title: "Pair Creativity With Systems",
      description: "Use checklists, deadlines, and simple routines to finish what you start.",
    },
  ],
  strengths: [
    {
      title: "Creative Momentum",
      description: "You generate ideas quickly and bring excitement to early-stage projects.",
    },
    {
      title: "Empathy",
      description: "You notice how people feel and can adjust your approach with care.",
    },
    {
      title: "Adaptability",
      description: "You can shift plans when circumstances change without getting stuck.",
    },
    {
      title: "Practical Drive",
      description: "You want ideas to become useful, not just impressive.",
    },
    {
      title: "Optimism",
      description: "Your energy helps teams keep moving during uncertain moments.",
    },
    {
      title: "Resourcefulness",
      description: "You can make progress with limited tools, time, or information.",
    },
  ],
  challenges: [
    {
      title: "Follow-Through",
      description: "New ideas may distract you before older commitments are complete.",
    },
    {
      title: "Overthinking",
      description: "You may replay decisions when the next useful step is already clear.",
    },
    {
      title: "Routine Fatigue",
      description: "Highly repetitive tasks can drain your energy if they feel disconnected from purpose.",
    },
    {
      title: "Sensitivity to Criticism",
      description: "Feedback can feel personal when you care deeply about the work.",
    },
  ],
  challenge_solutions: [
    {
      title: "Use Small Deadlines",
      description: "Break big ideas into short milestones so progress stays visible.",
    },
    {
      title: "Capture Ideas Separately",
      description: "Keep a parking lot for new ideas so they do not interrupt current work.",
    },
    {
      title: "Ask for Specific Feedback",
      description: "Request one improvement area at a time to make critique easier to act on.",
    },
    {
      title: "Protect Focus Blocks",
      description: "Schedule quiet time for execution after brainstorming or collaboration.",
    },
  ],
  quote: "Your best ideas become powerful when you give them a finish line.",
  closing_tip:
    "Use your energy to begin boldly, then rely on simple systems to finish with confidence.",
  career_path: {
    description:
      "You are suited to careers where imagination, practical execution, and people-facing impact overlap.",
    paths: [
      {
        title: "Product Design",
        description: "Shape useful experiences by combining user empathy, creativity, and iteration.",
      },
      {
        title: "Entrepreneurship",
        description: "Spot opportunities, build solutions, and rally others around a shared mission.",
      },
      {
        title: "Marketing & Brand Strategy",
        description: "Translate ideas into stories that people understand and remember.",
      },
      {
        title: "Education & Training",
        description: "Create engaging learning experiences that connect ideas to real life.",
      },
      {
        title: "Creative Technology",
        description: "Use digital tools, media, or systems to solve practical problems.",
      },
      {
        title: "Social Innovation",
        description: "Build programs or services that improve communities and everyday outcomes.",
      },
    ],
  },
  workplace_habit: {
    introduction:
      "At work, you are energized by purpose, variety, and progress. You like roles where you can contribute ideas, test solutions, and see how your effort helps people.",
    habits: [
      {
        title: "Brainstorm First, Structure Second",
        description: "You often need space to explore before choosing the strongest plan.",
        example: "You sketch three possible campaign ideas before selecting one to execute.",
      },
      {
        title: "Learn by Doing",
        description: "You understand systems faster when you can test them directly.",
        example: "Instead of reading every instruction first, you build a small prototype and improve it.",
      },
      {
        title: "Collaborate With Energy",
        description: "You enjoy exchanging ideas and helping others feel included.",
        example: "In a team meeting, you connect quiet suggestions into a stronger shared direction.",
      },
      {
        title: "Seek Meaning",
        description: "You work harder when you know why a task matters.",
        example: "A routine report becomes easier when you see how it helps students choose better paths.",
      },
    ],
    work_culture: [
      {
        title: "Best Culture",
        description: "Flexible, mission-driven teams with room for ownership and creative problem solving.",
      },
      {
        title: "Needs Support",
        description: "Clear deadlines, priority lists, and practical feedback help you stay consistent.",
      },
      {
        title: "Avoids",
        description: "Rigid environments with little context, little autonomy, or repetitive work only.",
      },
      {
        title: "Growth Focus",
        description: "Build routines that protect your best ideas from unfinished execution.",
      },
    ],
  },
  academic_habit: {
    introduction:
      "Academically, you learn best when lessons connect to real projects, discussion, and visible outcomes.",
    habits: [
      {
        title: "Interactive Learning",
        description: "You remember concepts better when you can discuss and apply them.",
        example: "You understand science faster through an experiment than a long lecture.",
      },
      {
        title: "Purpose-Led Study",
        description: "You focus more when the topic connects to a real future goal.",
        example: "You work harder on writing when it supports a presentation you care about.",
      },
    ],
    academic_culture: [
      {
        title: "Best Fit",
        description: "Project-based classes with discussion, experimentation, and mentoring.",
      },
      {
        title: "Growth Need",
        description: "Revision routines and deadlines help you complete strong ideas.",
      },
    ],
  },
  learning_style: {
    introduction:
      "Your learning style is active, social, and experimental. You benefit from examples, practice, and quick feedback.",
    data: [
      {
        title: "Prototype to Learn",
        description: "Build small versions of ideas to understand them faster.",
        example: "You create a sample presentation slide before writing the entire script.",
      },
      {
        title: "Discuss Out Loud",
        description: "Talking through ideas helps you organize your thinking.",
        example: "You explain a chapter to a friend and notice what you still need to review.",
      },
    ],
  },
  relationships: {
    introduction:
      "In relationships, you are warm, encouraging, and expressive. You like meaningful conversations and often help others see possibility in themselves.",
    strengths: {
      example:
        "A friend feels stuck, and you help them identify one practical step while reminding them of their strengths.",
      data: [
        {
          title: "Encouraging Presence",
          description: "You help others feel capable and supported.",
        },
        {
          title: "Open Communication",
          description: "You are willing to share ideas, feelings, and hopes honestly.",
        },
        {
          title: "Loyal Support",
          description: "You show up when people need motivation or practical help.",
        },
      ],
    },
    struggle: {
      example:
        "When feedback feels sharp, you may withdraw before realizing the person was trying to help.",
      data: [
        {
          title: "Taking Criticism Personally",
          description: "You may need time to separate feedback from rejection.",
        },
        {
          title: "Changing Priorities",
          description: "Your enthusiasm for new plans can make others unsure what to expect.",
        },
        {
          title: "Avoiding Conflict",
          description: "You may soften issues too much when a direct conversation would help.",
        },
      ],
    },
    dynamics: [
      {
        description: "You connect best with people who respect your ideas and help you stay grounded.",
      },
      {
        description: "You bring optimism to groups, especially when a situation feels uncertain.",
      },
      {
        description: "You value freedom, but you also appreciate people who keep commitments clear.",
      },
      {
        description: "You grow through honest feedback delivered with respect and context.",
      },
    ],
  },
  conclusion:
    "As a Visionary Doer, your power is the rare combination of imagination and movement. You can see what could be better, invite people into that possibility, and then build something useful from it. Your growth comes from finishing what matters most, protecting your focus, and treating feedback as a tool instead of a verdict.",
  concluding_quote: "Your vision is bold, but your action makes it real.",
};

export const dummyPreviewResult = {
  id: "preview-result",
  name: dummyDetailedReport.name,
  simple_desc: dummyDetailedReport.simple_desc,
  image_urls: dummyDetailedReport.image_urls,
  prediction: {
    mbti: {
      entity: {
        id: 8,
        name: "campaigner",
        code: "ENFP",
      },
      values: {
        INTROVERT: "28.0%",
        EXTROVERT: "72.0%",
        SENSING: "34.0%",
        INTUITION: "66.0%",
        THINKING: "39.0%",
        FEELING: "61.0%",
        JUDGING: "31.0%",
        PROSPECTING: "69.0%",
      },
    },
    holland: {
      hollandCode: "AES",
      entity: {
        id: 1,
        name: "artistic enterprising social",
        code: "AES",
      },
      values: {
        INVESTIGATIVE: "62.0%",
        REALISTIC: "58.0%",
        ARTISTIC: "88.0%",
        CONVENTIONAL: "36.0%",
        ENTERPRISING: "82.0%",
        SOCIAL: "76.0%",
      },
    },
  },
};
